import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageNavHeader from '../../components/layout/PageNavHeader';
import {
  PortfolioCompareCardButton,
  PortfolioCompareDock,
  compareCardWrapClass,
  dockPlFromContent,
} from '@shared/PortfolioCompareUx';
import { ComparisonAccountModeBadge } from '@shared/PortfolioComparison';
import {
  formatCurrency,
  formatMaybeCurrency,
  formatPercent,
  formatSignedCurrency,
} from '@shared/SharedAccountBody';
import type { RetailAccountContent, UserRetailAccount } from '@shared/types';
import { sortRetailAccountsLiveFirst } from '@shared/sortRetailAccounts';
import ComparePickCardSkeleton from './ComparePickCardSkeleton';
import {
  useRetailAccounts as useRetailAccountsQuery,
  useAccountContentQueries,
} from '../../queries/retailAccounts';

// Bottom padding so the floating compare dock never covers the last cards
// (was .nq-compare-page-pad).
const DOCK_PAD =
  'pb-[calc(148px+env(safe-area-inset-bottom,0px))] max-[640px]:pb-[calc(168px+env(safe-area-inset-bottom,0px))]';
// Shimmer-free skeleton block: token-driven pulse on the muted surface.
const SK = 'animate-pulse rounded bg-muted';

const accountLabel = (account: UserRetailAccount, content?: RetailAccountContent) =>
  account.nickname || content?.accountName || (account.isPaper ? 'Paper Trading' : 'Live Trading');

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const ComparePortfoliosPage = () => {
  const navigate = useNavigate();
  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(new Set());

  const { data: accounts = [], isLoading } = useRetailAccountsQuery();

  // Eagerly load content for all accounts so cards show data immediately
  const allIds = useMemo(() => new Set(accounts.map((a) => a.id)), [accounts]);
  const contentQueries = useAccountContentQueries(accounts, allIds);

  const contentById = useMemo(() => {
    const map: Record<string, RetailAccountContent | undefined> = {};
    accounts.forEach((acc, i) => { map[acc.id] = contentQueries[i]?.data; });
    return map;
  }, [accounts, contentQueries]);

  const contentLoadingById = useMemo(() => {
    const map: Record<string, boolean> = {};
    accounts.forEach((acc, i) => { map[acc.id] = !!contentQueries[i]?.isFetching; });
    return map;
  }, [accounts, contentQueries]);

  const sortedAccounts = useMemo(() => sortRetailAccountsLiveFirst(accounts), [accounts]);
  const selectedIds = useMemo(() => Array.from(selectedForCompare), [selectedForCompare]);

  const dockItems = useMemo(
    () =>
      selectedIds.map((id) => {
        const acc = accounts.find((a) => a.id === id);
        const content = contentById[id];
        const { plAmount, plPercent } = dockPlFromContent(content);
        return { id, label: acc ? accountLabel(acc, content) : id, plAmount, plPercent };
      }),
    [selectedIds, accounts, contentById],
  );

  const toggleSelection = (accountId: string) => {
    setSelectedForCompare((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const openComparison = () => {
    if (selectedIds.length < 2) {
      toast.error('Pick at least 2 portfolios to compare');
      return;
    }
    navigate(`/profile/portfolios/compare/view?ids=${selectedIds.join(',')}`);
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-[960px]">
        <div className="flex flex-col gap-2.5 py-1" aria-hidden>
          <div className={`${SK} h-3.5 w-[min(420px,72%)]`} />
          <div className={`${SK} h-7 w-[min(280px,50%)]`} />
        </div>
        <div className={`${SK} mb-4 h-3.5 w-[min(520px,90%)]`} />
        <div className="-mx-0.5 grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-5 px-0.5 py-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <ComparePickCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (accounts.length < 2) {
    return (
      <div className="w-full max-w-[960px]">
        <PageNavHeader
          crumbs={[
            { label: 'Profile', to: '/profile' },
            { label: 'My Portfolios', to: '/profile/portfolios' },
            { label: 'Compare Portfolios' },
          ]}
          title="Compare Portfolios"
          back={{ to: '/profile/portfolios', label: 'Back to portfolios' }}
        />
        <p className="m-0 mb-4 text-[0.9rem] leading-[1.45] text-muted-foreground">
          Link at least 2 portfolios to use comparison.
        </p>
      </div>
    );
  }

  return (
    <div
      className={[
        'w-full max-w-[960px]',
        selectedIds.length > 0 ? DOCK_PAD : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <PageNavHeader
        crumbs={[
          { label: 'Profile', to: '/profile' },
          { label: 'My Portfolios', to: '/profile/portfolios' },
          { label: 'Compare Portfolios' },
        ]}
        title="Compare Portfolios"
        back={{ to: '/profile/portfolios', label: 'Back to portfolios' }}
      />

      <p className="m-0 mb-4 text-[0.9rem] leading-[1.45] text-muted-foreground">
        Select two or more portfolios to compare performance, holdings, and
        overlap side by side.
      </p>

      <div className="-mx-0.5 grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-5 px-0.5 py-1">
        {sortedAccounts.map((acc) => {
          const content = contentById[acc.id];
          const contentPending = content === undefined && !!contentLoadingById[acc.id];
          const isSelected = selectedForCompare.has(acc.id);
          const { plAmount, plPercent } = dockPlFromContent(content);
          const plClass =
            plAmount === undefined
              ? 'text-muted-foreground'
              : plAmount > 0
                ? 'text-positive'
                : plAmount < 0
                  ? 'text-[color:var(--nq-down-soft)]'
                  : 'text-muted-foreground';
          const ring = isSelected
            ? acc.isPaper
              ? 'border-2 border-primary/55 shadow-[inset_0_0_0_1px_rgba(37,180,224,0.12),0_4px_16px_rgba(37,180,224,0.08)]'
              : 'border-2 border-secondary/70 shadow-[inset_0_0_0_1px_rgba(247,148,29,0.18),0_4px_16px_rgba(247,148,29,0.12)]'
            : 'border border-[color:rgba(255,255,255,0.08)] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

          return (
            <article
              key={acc.id}
              className={[
                'relative mt-[18px] flex flex-col gap-2 rounded-xl border-solid bg-[color:rgba(255,255,255,0.025)] px-3.5 pb-3 pt-3.5 transition-[border-color,box-shadow] duration-150',
                ring,
                compareCardWrapClass(isSelected, acc.isPaper),
              ]
                .filter(Boolean)
                .join(' ')}
              aria-busy={contentPending || undefined}
            >
              <PortfolioCompareCardButton
                selected={isSelected}
                onToggle={() => toggleSelection(acc.id)}
              />
              <div className="flex min-h-6 items-center gap-2">
                <h3 className="m-0 truncate text-[0.95rem] font-semibold text-foreground">
                  {accountLabel(acc, content)}
                </h3>
                <ComparisonAccountModeBadge account={acc} />
              </div>
              <div className="text-[1.35rem] font-bold leading-[1.1] tabular-nums text-foreground">
                {contentPending ? (
                  <div className={`${SK} h-7 w-[58%]`} aria-hidden />
                ) : (
                  formatMaybeCurrency(content?.totalValue)
                )}
              </div>
              <div className={`text-[0.85rem] font-semibold tabular-nums ${plClass}`}>
                {contentPending ? (
                  <div className={`${SK} h-3.5 w-[42%]`} aria-hidden />
                ) : plAmount !== undefined ? (
                  <>
                    {formatSignedCurrency(plAmount)}
                    {plPercent !== undefined && (
                      <span className="font-medium opacity-90">
                        {' '}({formatPercent(plPercent)})
                      </span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </div>
              <dl className="mt-1 grid grid-cols-2 gap-x-2.5 gap-y-1.5 border-t border-[color:rgba(255,255,255,0.06)] pt-2 [&_dd]:mt-0.5 [&_dd]:text-[0.78rem] [&_dd]:tabular-nums [&_dd]:text-foreground/80 [&_dt]:text-[0.68rem] [&_dt]:uppercase [&_dt]:tracking-[0.05em] [&_dt]:text-muted-foreground">
                <div className="min-w-0">
                  <dt>Started</dt>
                  <dd>
                    {contentPending ? (
                      <div className={`${SK} h-3 w-[68%]`} aria-hidden />
                    ) : (
                      formatDate(content?.trackRecordStartedAt ?? content?.tradingStartedAt)
                    )}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt>Last rebalanced</dt>
                  <dd>
                    {contentPending ? (
                      <div className={`${SK} h-3 w-[68%]`} aria-hidden />
                    ) : (
                      formatDate(acc.lastRebalancedAt)
                    )}
                  </dd>
                </div>
                <div className="col-span-2 min-w-0">
                  <dt>Invested</dt>
                  <dd>
                    {contentPending ? (
                      <div className={`${SK} h-3 w-[42%]`} aria-hidden />
                    ) : content?.stockValue !== undefined ? (
                      formatCurrency(content.stockValue)
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>

      <PortfolioCompareDock
        items={dockItems}
        onRemove={toggleSelection}
        onClear={() => setSelectedForCompare(new Set())}
        onCompare={openComparison}
      />
    </div>
  );
};

export default ComparePortfoliosPage;
